import {
  QUIZ_NEW_ANSWER_COUNT,
  QUIZ_OPTION_KEYS,
  QUIZ_QUESTION_TYPE,
  QUIZ_TRUE_FALSE_ANSWERS,
} from '../../common/constants';
import { getOptionEntries, isTrueFalseQuestion } from './grading';

/*
 * The API stores a question's answers as a dictionary keyed "A", "B", "C"…
 * The editor works with a plain ordered list instead, so adding or removing an
 * answer does not mean re-lettering everything by hand. These helpers convert
 * between the two shapes.
 */

/**
 * How a question says where it came from. Generated questions the learner has
 * not touched say nothing at all.
 *
 * Both shapes of question carry these two flags, so the editor and the quiz
 * itself label a question the same way — a learner who added a question while
 * editing sees it marked as theirs when they come to take it.
 *
 * @param {Object} question A quiz question, in either shape
 * @returns {String} The label, or an empty string
 */
export const getOriginLabel = ({ isManuallyAdded, isManuallyEdited }) => {
  if (isManuallyAdded) return 'Added by user';
  if (isManuallyEdited) return 'Edited by user';
  return '';
};

/**
 * Converts an API question into the shape the quiz editor edits.
 * @param {Object} question A quiz question with an options dictionary
 * @returns {Object} The editable question
 */
export const toEditableQuestion = (question) => {
  const entries = getOptionEntries(question);
  const correctIndex = entries.findIndex(([key]) => key === question.answer);

  return {
    id: question.id,
    question: question.question ?? '',
    hint: question.hint ?? '',
    explanation: question.explanation ?? '',
    answers: entries.map(([, text]) => text ?? ''),
    // A question whose answer key is missing falls back to the first answer.
    correctIndex: Math.max(0, correctIndex),
    /*
     * True/False is not a stored type — it is a question whose two answers are
     * True and False — but the editor has to know, because those answers are
     * fixed and cannot be rewritten.
     */
    isTrueFalse: isTrueFalseQuestion(entries),
    // Where the question came from: it labels the card, and a question the
    // learner wrote themselves is not held to the generated fields.
    isManuallyAdded: !!question.isManuallyAdded,
    isManuallyEdited: !!question.isManuallyEdited,
  };
};

/**
 * Converts an edited question back into the API's options-dictionary shape.
 * A question the learner has just added has no id yet; the backend assigns one.
 * @param {Object} editable An editable question
 * @returns {Object} The question in API shape
 */
export const fromEditableQuestion = (editable) => {
  const answers = editable.answers ?? [];
  const options = Object.fromEntries(
    answers.map((text, i) => [QUIZ_OPTION_KEYS[i], text?.trim() ?? '']),
  );
  const correctIndex = Math.min(editable.correctIndex ?? 0, answers.length - 1);

  return {
    id: editable.id,
    question: editable.question?.trim() ?? '',
    hint: editable.hint?.trim() ?? '',
    explanation: editable.explanation?.trim() ?? '',
    options,
    answer: QUIZ_OPTION_KEYS[correctIndex],
  };
};

/**
 * Whether a question has everything it needs to be saved.
 *
 * A generated question is held to its hint and explanation, which came with it
 * and should not be lost. One the learner added never had either, so it only
 * owes its question text and answers.
 * @param {Object} editableQuestion One of the editor's questions
 * @returns {Boolean} True when this question is complete
 */
const isQuestionComplete = ({ question, answers, hint, explanation, isManuallyAdded }) =>
  Boolean(
    question?.trim() &&
    answers?.length >= 2 &&
    answers.every((answer) => answer?.trim()) &&
    (isManuallyAdded || (hint?.trim() && explanation?.trim())),
  );

/**
 * The position of the first question that is missing something.
 *
 * react-hook-form only validates the fields it has mounted, and the editor pages
 * its questions — so most of the quiz is off screen and unchecked when Save is
 * pressed. The whole quiz is checked here instead.
 * @param {Array<Object>} editableQuestions The editor's questions
 * @returns {Number} The index of the first incomplete question, or -1 when all are complete
 */
export const findIncompleteQuestion = (editableQuestions = []) =>
  editableQuestions.findIndex((question) => !isQuestionComplete(question));

/**
 * Builds the blank question the plus button drops into the quiz. True/False
 * arrives with its answers already written, since those cannot be edited.
 * @param {String} type One of QUIZ_QUESTION_TYPE
 * @returns {Object} An editable question ready to be filled in
 */
export const createEmptyQuestion = (type) => {
  const isTrueFalse = type === QUIZ_QUESTION_TYPE.TRUE_FALSE;

  return {
    question: '',
    hint: '',
    explanation: '',
    answers: isTrueFalse ? [...QUIZ_TRUE_FALSE_ANSWERS] : Array(QUIZ_NEW_ANSWER_COUNT).fill(''),
    correctIndex: 0,
    isTrueFalse,
    isManuallyAdded: true,
    isManuallyEdited: false,
  };
};
